import { acceptAll, createApplicationMessage, createCommit, createGroup, decodeGroupState, defaultAuthenticationService, defaultKeyPackageEqualityConfig, defaultKeyRetentionConfig, defaultLifetimeConfig, defaultPaddingConfig, encodeGroupState, emptyPskIndex, joinGroup, processMessage, processPrivateMessage, } from 'ts-mls';
import { suite, utf8 } from './suite.js';
/** Gründet die Gruppe eines Raums. Nur das erste Gerät tut das. */
export async function gruppeGruenden(roomId, eigenes) {
    const cs = await suite();
    const state = await createGroup(utf8.encode(roomId), eigenes.publicPackage, eigenes.privatePackage, [], cs);
    return { state };
}
/** Nimmt ein Gerät auf. Das Ergebnis enthält Commit *und* Welcome. */
export async function mitgliedHinzufuegen(zustand, keyPackage) {
    const cs = await suite();
    const res = await createCommit({ state: zustand.state, cipherSuite: cs }, {
        extraProposals: [{ proposalType: 'add', add: { keyPackage } }],
        // Der Ratchet-Tree reist im Welcome mit. Ohne diese Option müsste er
        // separat übertragen werden — und ts-mls exportiert dafür keinen Encoder.
        ratchetTreeExtension: true,
    });
    return {
        zustand: { state: res.newState },
        commit: res.commit,
        welcome: res.welcome,
        ratchetTree: res.newState.ratchetTree,
    };
}
/**
 * Entfernt ein Gerät.
 *
 * Das ist die Operation, wegen der die Architektur MLS gewählt hat (ADR-003):
 * Nach dem Commit kann das entfernte Gerät keine neue Epoche mehr entschlüsseln.
 * „Gerät sperren" wirkt damit kryptografisch, nicht nur als Server-Zusage.
 */
export async function mitgliedEntfernen(zustand, identitaet) {
    const cs = await suite();
    const knoten = blattIndexVon(zustand, identitaet);
    if (knoten === null)
        throw new GruppenFehler('Dieses Gerät ist kein Mitglied der Gruppe');
    const res = await createCommit({ state: zustand.state, cipherSuite: cs }, {
        extraProposals: [{ proposalType: 'remove', remove: { removed: knoten / 2 } }],
    });
    return {
        zustand: { state: res.newState },
        commit: res.commit,
        welcome: res.welcome,
        ratchetTree: res.newState.ratchetTree,
    };
}
/**
 * Erzwungene Schlüsselrotation ohne Mitgliederwechsel.
 *
 * Das ist die Post-Compromise Security aus SECURITY.md §3.3: Selbst wenn ein
 * Schlüssel abgeflossen ist und niemand es merkt, ist er nach der nächsten
 * Rotation wertlos. Läuft zeitgesteuert, alle 7 Tage je aktivem Gerät.
 */
export async function schluesselErneuern(zustand) {
    const cs = await suite();
    const res = await createCommit({ state: zustand.state, cipherSuite: cs }, {});
    return {
        zustand: { state: res.newState },
        commit: res.commit,
        welcome: res.welcome,
        ratchetTree: res.newState.ratchetTree,
    };
}
/** Tritt einer Gruppe über ein Welcome bei. */
export async function gruppeBeitreten(welcome, eigenes, ratchetTree) {
    const cs = await suite();
    const state = await joinGroup(welcome, eigenes.publicPackage, eigenes.privatePackage, emptyPskIndex, cs, ratchetTree);
    return { state };
}
/** Verschlüsselt eine Nachricht. Der Klartext verlässt das Gerät nicht. */
export async function verschluesseln(zustand, klartext) {
    const cs = await suite();
    const res = await createApplicationMessage(zustand.state, utf8.encode(klartext), cs);
    return {
        zustand: { state: res.newState },
        // createApplicationMessage liefert die PrivateMessage; für die Leitung
        // wird sie wie jede andere MLS-Nachricht gerahmt.
        nachricht: { wireformat: 'mls_private_message', privateMessage: res.privateMessage,
            version: 'mls10' },
    };
}
/**
 * Verarbeitet alles, was hereinkommt — Anwendungsnachricht oder Commit.
 *
 * Wirft, wenn die Nachricht nicht zur eigenen Epoche passt. Das ist gewollt:
 * Ein Gerät, das einen Commit verpasst hat, muss ihn nachholen, statt mit einem
 * halb kaputten Zustand weiterzumachen.
 */
export async function empfangen(zustand, nachricht) {
    const cs = await suite();
    const res = await processMessage(nachricht, zustand.state, emptyPskIndex, acceptAll, cs);
    if (res.kind === 'applicationMessage') {
        return {
            art: 'nachricht',
            text: utf8.decode(res.message),
            zustand: { state: res.newState },
        };
    }
    return { art: 'zustandswechsel', zustand: { state: res.newState } };
}
/** Kurzform für den häufigsten Fall: eine reine Anwendungsnachricht. */
export async function entschluesseln(zustand, privateMessage) {
    const cs = await suite();
    const res = await processPrivateMessage(zustand.state, privateMessage, emptyPskIndex, cs);
    if (res.kind === 'applicationMessage') {
        return {
            art: 'nachricht',
            text: utf8.decode(res.message),
            zustand: { state: res.newState },
        };
    }
    return { art: 'zustandswechsel', zustand: { state: res.newState } };
}
export class GruppenFehler extends Error {
}
/**
 * Ein MLS-Credential kann laut Standard auch ein X.509-Zertifikat sein. Wir
 * vergeben ausschließlich `basic`-Credentials (device.ts) — trifft trotzdem
 * etwas anderes ein, ist das kein Mitglied unserer Gruppen und wird ignoriert,
 * statt mit einem Typfehler zur Laufzeit zu enden.
 */
function identitaetVon(credential) {
    return credential.credentialType === 'basic' ? utf8.decode(credential.identity) : null;
}
// --- Auskünfte über die Gruppe ----------------------------------------------
export function epoche(zustand) {
    return zustand.state.groupContext.epoch;
}
/** Alle Geräte-Identitäten in der Gruppe — für die Anzeige und den Abgleich. */
export function mitglieder(zustand) {
    const out = [];
    for (const knoten of zustand.state.ratchetTree) {
        if (knoten?.nodeType !== 'leaf')
            continue;
        const id = identitaetVon(knoten.leaf.credential);
        if (id !== null)
            out.push(id);
    }
    return out;
}
/** Signaturschlüssel eines Mitglieds — Eingabe für die Sicherheitsnummer. */
export function signaturSchluessel(zustand, identitaet) {
    for (const knoten of zustand.state.ratchetTree) {
        if (knoten?.nodeType !== 'leaf')
            continue;
        if (identitaetVon(knoten.leaf.credential) === identitaet) {
            return knoten.leaf.signaturePublicKey;
        }
    }
    return null;
}
function blattIndexVon(zustand, identitaet) {
    const index = zustand.state.ratchetTree.findIndex((n) => n?.nodeType === 'leaf' && identitaetVon(n.leaf.credential) === identitaet);
    return index < 0 ? null : index;
}
// --- Zustand sichern und zurückholen -----------------------------------------
/**
 * Der Gruppenzustand als Bytes — und zurück.
 *
 * ## Warum das hier steht und nicht im Web-Paket
 *
 * `encodeGroupState`/`decodeGroupState` aus ts-mls arbeiten auf `GroupState`.
 * Zum Senden braucht ts-mls aber einen `ClientState`, und der ist
 * `GroupState & { clientConfig }`. Die Konfiguration ist **nicht Teil der
 * Kodierung** — sie ist Verhalten (Polsterung, Schlüsselaufbewahrung,
 * Lebensdauerprüfung), keine Zustandsinformation.
 *
 * Im Web-Paket stand deshalb ein `as GruppenZustand` auf dem dekodierten Wert,
 * und der Cast verdeckte die Lücke. Die Folge war ein Fehler, der erst nach
 * einem Neustart der App auftrat und nur beim SENDEN:
 *
 *     TypeError: Cannot read properties of undefined (reading 'paddingConfig')
 *
 * Nachrichten LESEN ging weiter — deshalb sah der Chat funktionsfähig aus.
 * Gefunden beim ersten echten Durchlauf im Browser.
 *
 * Die Konfiguration wird beim Zurückholen wieder angehängt. Sie ist auf allen
 * Geräten dieselbe; würde sie das eines Tages nicht mehr sein, gehörte sie
 * ausdrücklich mitgespeichert und nicht stillschweigend ergänzt.
 */
/**
 * Dieselben Werte, die ts-mls beim Gründen einer Gruppe selbst einsetzt
 * (`defaultClientConfig`) — das Paket exportiert sie nur einzeln.
 */
const STANDARD_KONFIG = {
    keyRetentionConfig: defaultKeyRetentionConfig,
    lifetimeConfig: defaultLifetimeConfig,
    keyPackageEqualityConfig: defaultKeyPackageEqualityConfig,
    paddingConfig: defaultPaddingConfig,
    authService: defaultAuthenticationService,
};
export function zustandAlsBytes(zustand) {
    return encodeGroupState(zustand.state);
}
export function zustandAusBytes(bytes) {
    const dekodiert = decodeGroupState(bytes, 0);
    if (!dekodiert)
        return null;
    return { state: { ...dekodiert[0], clientConfig: STANDARD_KONFIG } };
}
//# sourceMappingURL=group.js.map